import { AssetService } from '@/api/services/AssetService';
import store from '@/store';
import authModule from '@/store/modules/auth';
import { getModule } from 'vuex-module-decorators';
import { AssetSearchCriteria, AssetsCatalogDto } from '@/api';
import callWithContext from '@/api/call-with-context';
import { ToolDefinition } from '../types';

const searchAssetsTool: ToolDefinition = {
  name: 'search_assets',
  description: 'Search assets (tables, dashboards, pipelines, ML models). Use for counts, lists, details, health status, monitoring coverage. Filters: owner=["00000000-0000-0000-0000-000000000000"] = no owners; healthStatus=["UNMONITORED"] = unmonitored. Response: catalogFilters (precise counts), searchAssets (paginated, totalElements). Fields: name, id, urn, type, healthStatus, owners, tags, terms, domains. Assets/monitors separate. Use domain (name) to filter. For custom metadata filters, use customMetadataValues with entry IDs (UUIDs). IMPORTANT: For filter breakdowns (e.g., counts by type or status), use catalogFilters.children[].results directly if available. Do NOT make additional filtered searches if results are provided in catalogFilters.',
  input_schema: {
    type: 'object',
    properties: {
      textSearch: {
        type: 'string',
        description: 'Match text in asset names',
      },
      assetType: {
        type: 'array',
        description: 'Types: TABLE_AND_VIEW, PIPELINE, DASHBOARD, ML_MODEL',
        items: {
          type: 'string',
        },
      },
      healthStatus: {
        type: 'array',
        description: 'Status: URGENT_INCIDENTS, HIGH_RISK_INCIDENTS, NO_INCIDENTS, NOT_MONITORED, UNMONITORED, UNSUPPORTED',
        items: {
          type: 'string',
        },
      },
      owner: {
        type: 'array',
        description: 'Owner UUIDs/emails. ["00000000-0000-0000-0000-000000000000"] = no owners',
        items: {
          type: 'string',
        },
      },
      tags: {
        type: 'array',
        description: 'Tag names or IDs',
        items: {
          type: 'string',
        },
      },
      levelOfUsage: {
        type: 'array',
        description: 'Usage: UNSUPPORTED, LOW, MEDIUM, HIGH',
        items: {
          type: 'string',
        },
      },
      sourceId: {
        type: 'array',
        description: 'Datasource IDs',
        items: {
          type: 'string',
        },
      },
      terms: {
        type: 'array',
        description: 'Term names or IDs',
        items: {
          type: 'string',
        },
      },
      customMetadataValues: {
        type: 'array',
        description: 'Custom metadata entry IDs',
        items: {
          type: 'string',
        },
      },
      domain: {
        type: 'string',
        description: 'Domain name (default: selected domain)',
      },
      ingestionMethod: {
        type: 'array',
        description: 'Ingestion: DECLARATIVE, SIFFLET_SOURCED',
        items: {
          type: 'string',
        },
      },
      page: {
        type: 'integer',
        description: 'Page number (default: 0)',
      },
      itemsPerPage: {
        type: 'integer',
        description: 'Items per page (default: 25)',
      },
      sort: {
        type: 'array',
        description: 'Sort: [{"field": "name", "direction": "ASC"}]. Fields: name, relevance. Directions: ASC, DESC',
        items: {
          type: 'object',
        },
      },
    },
    required: [],
  },
};

/**
 * Execute the search_assets tool
 */
export async function executeSearchAssets(params: any): Promise<AssetsCatalogDto | { error: string }> {
  try {
    // Get the auth module to access selected domain
    const auth = getModule(authModule, store);
    const selectedDomain = auth.selectedDomain?.name;

    // Transform params to AssetSearchCriteria format
    const {
      page, itemsPerPage, sort, domain, owner, assetType, healthStatus, levelOfUsage, sourceId, tags, terms, textSearch, customMetadataValues, ...otherParams
    } = params;

    const requestBody: AssetSearchCriteria = {
      page: page || 0,
      itemsPerPage: itemsPerPage || 25,
      sort: sort || ['relevance,ASC'],
      domain: domain || selectedDomain,
      ...(owner && { owner }),
      ...(assetType && { assetFilterType: assetType }),
      ...(healthStatus && { healthStatus }),
      ...(levelOfUsage && { usage: levelOfUsage }),
      ...(sourceId && { datasource: sourceId }),
      ...(tags && { tag: tags }),
      ...(terms && { term: terms }),
      ...(textSearch && { textSearch }),
      ...(customMetadataValues && { customMetadataEntry: customMetadataValues }),
      ...otherParams,
    };

    const result = await callWithContext('sifflet-ai-chat', () => AssetService.getAllAssets({
      requestBody,
    }));

    return result;
  } catch (error) {
    return { error: (error as Error).message };
  }
}

export default searchAssetsTool;
